import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NextPage } from "next";
import {
  Box,
  Flex,
  Text,
  Center,
  Divider,
  Show,
  Container,
} from "@chakra-ui/react";

// Components
import { PageContext, Footer, Header, useStaderTVL, BreadCrum } from "mlp-sdk";

// Providers
import { PageProvider } from "providers/PageProvider";

// Utils
import { fetchPageData, prepareNavData } from "mlp-sdk/utils/common";

import navData from "@constants/navData.json";
import PageHead from "@components/PageHead/PageHead";

let timerId = -1;

const TermsOfService = () => {
  const { page } = useContext(PageContext);

  const _pageData = page.data.pageData;

  const pageData = _pageData
    ? _pageData.tos_data?.tos_details?.reduce(
        (acc: any, curr: any) =>
          acc.set(curr.title.toLowerCase().replaceAll(" ", "-"), curr),
        new Map()
      )
    : new Map();

  const items = pageData ? Array.from(pageData.entries()) : [];
  const sidebarMenus = items.map((item: any) => item[0]);

  const [activeMenuKey, setActiveMenuKey] = useState(sidebarMenus[0]);
  const [isNavigating, setIsNavigating] = useState(false);

  const { tvl } = useStaderTVL();

  const { navData, networks } = useMemo(
    () => prepareNavData(page.data.navData, tvl),
    [page.data.navData, tvl]
  );

  const onSubHeaderChange = useCallback((key: string) => {
    setIsNavigating(true);
    setActiveMenuKey(key);

    clearTimeout(timerId);

    const section = document.querySelector(
      `#${key.toLowerCase().replaceAll(" ", "-")}`
    ) as HTMLElement | null;

    section &&
      section.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });

    timerId = window.setTimeout(() => setIsNavigating(false), 3000);
  }, []);

  useEffect(() => {
    const mainElements = sidebarMenus.map((item: string) =>
      document.getElementById(item.toLowerCase().replaceAll(" ", "-"))
    );

    const observer = new IntersectionObserver(
      (entries) => {
        for (let entry of entries) {
          if (entry.isIntersecting && !isNavigating) {
            setActiveMenuKey(entry.target.id);
          }
        }
      },
      {
        threshold: window?.innerWidth > 992 ? 0.3 : 0.1,
      }
    );

    mainElements.forEach((element) => element && observer.observe(element));

    return () => {
      mainElements.forEach((element) => element && observer.unobserve(element));
    };
  }, [sidebarMenus, isNavigating]);

  return (
    <>
      <PageHead page="terms-of-service" />

      <Header data={navData} networks={networks} tvl={tvl} />
      <Container px={{ base: 4, lg: "inherit" }} mt="24px">
        <Show below="lg">
          <BreadCrum />
        </Show>
        <Flex
          w={{ base: "100%" }}
          gap="85px"
          textAlign={{ base: "center", lg: "left" }}
        >
          <Box display={{ base: "none", lg: "block" }} minW={280} />
          <Box>
            <Show above="lg">
              <BreadCrum ml={0} mb="16px" />
            </Show>
            <Text mb={{ base: 4, lg: 12 }} textStyle={{ base: "h2", lg: "h1" }}>
              Terms of service
            </Text>
          </Box>
        </Flex>
        <Show below="lg">
          <Box position="sticky" top="57px">
            <Box bg="white">
              <Flex
                w="100%"
                gap="1rem"
                justifyContent={{ base: "flex-start", lg: "center" }}
                overflowX="auto"
              >
                {sidebarMenus.map((menu) => (
                  <Center
                    key={menu}
                    cursor="pointer"
                    flexDir="column"
                    p="16px 12px 0"
                    minW="fit-content"
                    onClick={() => {
                      onSubHeaderChange(menu);
                    }}
                  >
                    <Text
                      color={activeMenuKey === menu ? "black" : "textSecondary"}
                      mb="1rem"
                      textStyle="p12"
                      fontWeight={500}
                    >
                      {pageData.get(menu).title}
                    </Text>
                    <Divider
                      w="80%"
                      border="1px solid"
                      borderColor={
                        activeMenuKey === menu ? "#07C166" : "transparent"
                      }
                      borderTopRadius={4}
                    />
                  </Center>
                ))}
              </Flex>
            </Box>
            <Divider
              border="1px solid"
              borderColor="#AEDFCA"
              w={{ base: "100%", lg: "100vw" }}
            />
          </Box>
        </Show>
        <Flex
          w="100%"
          gap="85px"
          justifyContent="space-between"
          textAlign="left"
        >
          <Show above="lg">
            <Box minW="280px">
              <Flex gap="20px" flexDir="column" position="sticky" top="80px">
                {sidebarMenus.map((menu: string) => (
                  <Text
                    key={menu}
                    fontWeight="600"
                    fontSize="p16"
                    cursor="pointer"
                    onClick={() => onSubHeaderChange(menu)}
                    color={
                      activeMenuKey === menu ? "textPrimary" : "textTertiary"
                    }
                  >
                    {pageData.get(menu).title}
                  </Text>
                ))}
              </Flex>
            </Box>
          </Show>

          <Box mt={{ base: 8, lg: 0 }}>
            {sidebarMenus?.map((menu: string) => {
              const description = pageData.get(menu).description;
              return (
                <Box
                  id={menu.toLowerCase().replaceAll(" ", "-")}
                  key={menu}
                  scrollMargin={{ base: "128px", lg: "72px" }}
                >
                  <Text fontWeight="700" fontSize="24px" lineHeight="28px">
                    {pageData.get(menu).title}
                  </Text>
                  <Box
                    key={description}
                    mt={6}
                    textStyle={{ base: "p12", lg: "p16" }}
                    fontWeight={500}
                    color="textSecondary"
                    dangerouslySetInnerHTML={{ __html: description }}
                    sx={{
                      "& ol": {
                        pl: 4,
                      },
                    }}
                  />
                  <Divider my="64px" color="divider" />
                </Box>
              );
            })}
          </Box>
        </Flex>
      </Container>
      <Footer data={navData} />
    </>
  );
};

const TermsOfServicePage: NextPage = ({ page }: any) => {
  return (
    <PageProvider page={page}>
      <TermsOfService />
    </PageProvider>
  );
};

export async function getStaticProps() {
  const [pageRes] = await Promise.all([fetchPageData("landing-tos-page")]);

  const pageData = pageRes.data?.attributes || null;
  const pageError = pageRes.error || null;

  return {
    props: {
      page: {
        data: {
          navData,
          pageData: pageData,
        },
        error: { navError: null, pageError },
      },
    },
  };
}

export default TermsOfServicePage;
